<script setup lang="ts">
  import LogicalOperatorSelect from '@/components/form-wizard/LogicalOperatorSelect.vue'
  import MappingModal from '@/components/form-wizard/MappingModal.vue'
  import Predicates from '@/components/form-wizard/Predicates.vue'
  import DynamicVariableInput from '@/components/form/DynamicVariableInput.vue'
  import FlowPathInput from '@/components/form/FlowPathInput.vue'
  import {
    FormFieldContainer,
    InputField,
    ORDER_SETTINGS,
    SwitchButton,
    DropDown,
    UiButton,
    useXPathAction,
    type ListBrowser,
    type Nullish,
    type OrderSetting,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, reactive, ref } from 'vue'
  const showMapping = ref(false)

  const field = defineModel<ListBrowser>({ required: true })
  const xpath = useXPathAction()

  const touched = reactive({
    storeLocation: false,
    listSourcePath: false,
  })

  const item = computed((): Record<string, unknown> | undefined => {
    const valueArray = xpath.resolvePath(field.value.listSourcePath)
    if (!Array.isArray(valueArray)) {
      return undefined
    }
    const value = valueArray[0]
    return value
  })

  function validateRequired(key: 'storeLocation' | 'listSourcePath', message: string) {
    return touched[key] && field.value?.type === 'listBrowser' && !field.value?.[key]?.trim()
      ? message
      : null
  }

  function addDescriptor() {
    field.value.descriptorTemplates.push('')
  }
  function removeDescriptor(index: number) {
    field.value.descriptorTemplates.splice(index, 1)
  }

  const groupByText = computed(() => (field.value.groupByFields ?? []).join(', '))

  function setGroupBy(input: string | number | Nullish) {
    if (input == null) {
      return
    }
    const value = String(input)
    field.value.groupByFields = value
      .split(',')
      .map((entry: string) => entry.trim())
      .filter(Boolean)
  }
</script>

<template>
  <div class="field-config-container">
    <FormFieldContainer label="Label">
      <FlowPathInput
        placeholder="e.g. ${shipmentTemplate.shipment.name}"
        hint="Field label. Supports ${...} interpolation."
        v-model="field.label"
        :optional="true"
        type="text"
        :validate-paths="true"
        :context="item"
      />
    </FormFieldContainer>

    <FormFieldContainer
      label="DTO Target Path"
      :error="validateRequired('storeLocation', 'DTO Target Path is required')"
    >
      <InputField
        placeholder="DTO Target Path"
        hint="Where the mapped result is stored."
        v-model="field.storeLocation"
        type="text"
        @blur="touched.storeLocation = true"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Auto Complete">
      <SwitchButton v-model="field.autoComplete" size="sm" />
    </FormFieldContainer>

    <DynamicVariableInput
      label="List source path"
      :error="validateRequired('listSourcePath', 'List source path is required')"
      placeholder="context.path.to.list"
      hint="Path to the array to browse."
      v-model="field.listSourcePath"
      type="text"
      @blur="touched.listSourcePath = true"
      :optional="false"
      :validate-paths="true"
    />

    <FormFieldContainer label="Search requires exact match">
      <SwitchButton v-model="field.exactMatch" size="sm" />
    </FormFieldContainer>

    <FlowPathInput
      v-if="field.exactMatch"
      label="Exacte match field"
      placeholder="e.g. ${shipmentTemplate.shipment.name}"
      hint="Card title. Supports ${...} interpolation."
      v-model="field.exactMatchField"
      :optional="true"
      type="text"
      :validate-paths="true"
      :context="item"
    />

    <FormFieldContainer label="Show previous selected">
      <SwitchButton v-model="field.showPreviousSelected" size="sm" />
    </FormFieldContainer>

    <FlowPathInput
      v-if="field.showPreviousSelected"
      label="Label for previous selected"
      placeholder="e.g. ${shipmentTemplate.shipment.name}"
      hint="Card title. Supports ${...} interpolation."
      v-model="field.previousSelectedLabel"
      :optional="true"
      type="text"
      :validate-paths="true"
      :context="item"
    />

    <DropDown
      :required="true"
      label="Order Setting"
      :modelValue="{ value: field.orderSetting }"
      idKey="value"
      :data="
        ORDER_SETTINGS.map(value => ({
          value,
        }))
      "
      @update:modelValue="(value: { value: OrderSetting }) => (field.orderSetting = value.value)"
    >
      <template #item="{ item }">{{ item.value ?? 'Selecteer een optie' }}</template>
    </DropDown>

    <FlowPathInput
      v-if="field.orderSetting != null && field.orderSetting !== 'OFF'"
      label="What key to order the list on"
      placeholder="e.g. path.to.key"
      hint="Card title. Supports xpath interpolation."
      v-model="field.orderKey"
      :optional="true"
      type="text"
      :validate-paths="true"
      :context="item"
    />

    <FlowPathInput
      label="Title template"
      placeholder="e.g. ${shipmentTemplate.shipment.name}"
      hint="Card title. Supports ${...} interpolation."
      v-model="field.titleTemplate"
      :optional="true"
      type="text"
      :validate-paths="true"
      :context="item"
    />

    <FormFieldContainer label="Descriptors">
      <div class="repeatable">
        <div v-for="(_, index) in field.descriptorTemplates" :key="index" class="repeatable-row">
          <FlowPathInput
            :optional="false"
            :context="item"
            placeholder="e.g. Owner: ${shipmentTemplate.owner.name}"
            v-model="field.descriptorTemplates[index]"
            type="text"
            :validate-paths="true"
          />
          <button class="remove-btn" type="button" @click="removeDescriptor(index)">×</button>
        </div>
        <button class="add-btn" type="button" @click="addDescriptor">+ Add descriptor</button>
      </div>
    </FormFieldContainer>

    <LogicalOperatorSelect v-model="field.predicateCombinator" label="Filter Logic" />

    <Predicates v-model="field.predicates" :context="item" />

    <FormFieldContainer label="Mapping">
      <UiButton label="Configure mapping" @click="showMapping = true" />
    </FormFieldContainer>
    <MappingModal
      v-model="field.mappingTemplate"
      v-model:show="showMapping"
      :list-source-path="field.listSourcePath"
    />

    <FormFieldContainer label="Group by">
      <InputField
        placeholder="e.g. projectId, projectName"
        hint="Comma-separated fields. Result keeps only these."
        :model-value="groupByText"
        @update:model-value="setGroupBy"
        type="text"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Description">
      <FlowPathInput
        placeholder="Helpful text under the field"
        v-model="field.description"
        :optional="true"
        :validate-paths="true"
        :context="item"
      />
    </FormFieldContainer>

    <FormFieldContainer label="Error message">
      <FlowPathInput
        placeholder="Error message under the field"
        v-model="field.error"
        :optional="true"
        :validate-paths="true"
        :context="item"
      />
    </FormFieldContainer>
  </div>
</template>

<style scoped>
  .field-config-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
  .add-btn {
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0.25rem 0;
    color: var(--colours-brand-colour-1);
    font-size: 0.875rem;
    cursor: pointer;
  }

  .remove-btn {
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgb(209, 213, 219);
    border-radius: 0.375rem;
    background: var(--colours-grey-1);
    color: rgb(107, 114, 128);
    cursor: pointer;
    line-height: 1;
  }

  .repeatable {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .repeatable-row {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.5rem;
    align-items: center;
  }

  .add-btn {
    align-self: flex-start;
    background: none;
    border: none;
    padding: 0.25rem 0;
    color: var(--colours-brand-colour-1);
    font-size: 0.875rem;
    cursor: pointer;
  }

  .remove-btn {
    width: 1.75rem;
    height: 1.75rem;
    border: 1px solid rgb(209, 213, 219);
    border-radius: 0.375rem;
    background: var(--colours-grey-1);
    color: rgb(107, 114, 128);
    cursor: pointer;
    line-height: 1;
  }
</style>
